import * as React from 'react';
import { IndexRedirect, IndexRoute, Redirect, Route } from 'react-router';

import { ROUTE_ARGUMENTS } from 'bundles/admin-shared/analytics/constants';
import AdminHome from 'bundles/admin-v2/components/AdminHome';
import AdminV2App from 'bundles/admin-v2/components/AdminV2App';
import {
  ACCEPT_INVITE,
  ANALYTICS,
  COURSES,
  HOME,
  INSTITUTION,
  MY_GROUP,
  PROGRAMS,
  ROOT,
  SPECIALIZATIONS,
} from 'bundles/admin-v2/constants/routes';
import AuthoringAiToolsRootRoute from 'bundles/authoring-ai-tools/routes';
import loadOnRoute from 'bundles/common/components/loadOnRoute';
import {
  DiscoverExternalAppsPage,
  ExternalAppsPage,
  ManageExternalAppsPage,
} from 'bundles/manage-external-apps/routes';
import {
  CAMPAIGNS,
  CHANNELS,
  COURSE_MARKETING,
  COURSE_VIEW,
  CUSTOM_ROLES,
  DISBURSEMENTS,
  DISCOVER_EXTERNAL_APPS,
  EXPORTS,
  EXTERNAL_APPS,
  INSTITUTION_ASSETS,
  MANAGE_EXTERNAL_APPS,
  MONITOR,
  ORG,
  ORG_LEARNERS,
  OVERVIEW,
  PROMOTIONS,
  PROMOTION_VIEW,
  REACH,
  SETTINGS,
  SPECIALIZATION_ANALYTICS,
  SPECIALIZATION_CAMPAIGNS,
  SPECIALIZATION_CHANNELS,
  SPECIALIZATION_MARKETING,
  SPECIALIZATION_PROMOTION,
  SPECIALIZATION_SUMMARY,
  STAFF,
  SUMMARY,
  UNIVERSITY_ASSETS,
} from 'bundles/teach-partner-2018/constants/RouteNames';

// Home
const CoursesPage = loadOnRoute(() => import('bundles/admin-v2/components/CoursesPage'));
const ProjectsPage = loadOnRoute(() => import('bundles/admin-v2/components/ProjectsPage'));

const ProgramsPage = loadOnRoute(() => import('bundles/admin-v2/components/ProgramsPage'));
const GroupDeprecationPage = loadOnRoute(() => import('bundles/admin/components/GroupDeprecationPage'));
const SpecializationsPage = loadOnRoute(() => import('bundles/admin-v2/components/SpecializationsPage'));

// Institutions
const InstitutionsAdmin = loadOnRoute(() => import('bundles/admin/components/InstitutionsAdmin'));
const StaffManagementInstitution = loadOnRoute(
  () => import('bundles/teach-partner-2018/components/StaffManagementInstitution')
);
const PartnerEditor = loadOnRoute(() => import('bundles/author-partner/components/PartnerEditor'));
const CustomRolesManagementPage = loadOnRoute(
  () => import('bundles/teach-partner-2018/custom-roles/components/CustomRolesManagementPage')
);
const ExportsPartnerPage = loadOnRoute(() => import('bundles/teach-partner-2018/components/ExportsPartnerPage'));
const MarketingEmailsTool = loadOnRoute(() => import('bundles/admin-marketing-emails/components/MarketingEmailsTool'));

// Marketing
const CourseMarketing = loadOnRoute(() => import('bundles/teach-partner-2018/components/CourseMarketing'));
const SpecializationMarketing = loadOnRoute(
  () => import('bundles/teach-partner-2018/components/SpecializationMarketing')
);

// Analytics
const MonitorPartnerPage = loadOnRoute(() => import('bundles/teach-partner-2018/components/MonitorPartnerPage'));
const ReachPartnerPage = loadOnRoute(() => import('bundles/teach-partner-2018/components/ReachPartnerPage'));
const DisbursementsPartnerPage = loadOnRoute(
  () => import('bundles/teach-partner-2018/components/DisbursementsPartnerPage')
);
const MarketingSummaryPage = loadOnRoute(() => import('bundles/teach-partner-2018/components/MarketingSummaryPage'));
const ChannelsAnalyticsPage = loadOnRoute(() => import('bundles/teach-partner-2018/components/ChannelsAnalyticsPage'));
const CampaignsAnalyticsPage = loadOnRoute(
  () => import('bundles/teach-partner-2018/components/CampaignsAnalyticsPage')
);
const CoursesAnalyticsPage = loadOnRoute(() => import('bundles/teach-partner-2018/components/CoursesAnalyticsPage'));
const PromotionsAnalyticsPage = loadOnRoute(
  () => import('bundles/teach-partner-2018/components/PromotionsAnalyticsPage')
);

const S12nSummaryPage = loadOnRoute(() => import('bundles/teach-partner-2018/components/S12nSummaryPage'));
const S12nChannelsAnalyticsPage = loadOnRoute(
  () => import('bundles/teach-partner-2018/components/S12nChannelsAnalyticsPage')
);
const S12nCampaignsAnalyticsPage = loadOnRoute(
  () => import('bundles/teach-partner-2018/components/S12nCampaignsAnalyticsPage')
);
const S12nAnalyticsPage = loadOnRoute(() => import('bundles/teach-partner-2018/components/S12nAnalyticsPage'));
const S12nPromotionsAnalyticsPage = loadOnRoute(
  () => import('bundles/teach-partner-2018/components/S12nPromotionsAnalyticsPage')
);
const OrgLearnersAnalyticsPage = loadOnRoute(
  () => import('bundles/teach-partner-2018/components/OrgLearnersAnalyticsPage')
);
const OrgCoursesAnalyticsPage = loadOnRoute(
  () => import('bundles/teach-partner-2018/components/OrgCoursesAnalyticsPage')
);

const AcceptInvitePage = loadOnRoute(() => import('bundles/admin-v2/components/accept-invite/AcceptInvitePage'));

// Errors
const NotFound = loadOnRoute(() => import('bundles/phoenix/components/NotFound'));

export default (
  <Route path={`/admin-v2/:${ROUTE_ARGUMENTS.INSTITUTION_SLUG}`} component={AdminV2App} name={ROOT}>
    <Route name={ACCEPT_INVITE} path={ACCEPT_INVITE} getComponent={AcceptInvitePage} />

    {/* MY_GROUPS */}
    <Route name={MY_GROUP} path={MY_GROUP} getComponent={GroupDeprecationPage} />

    {/* Home */}
    <Route path={HOME} name={HOME} component={AdminHome}>
      <IndexRedirect to={COURSES} />
      <Route path={COURSES} name={COURSES} getComponent={CoursesPage} />
      <Route path="projects" name="projects" getComponent={ProjectsPage} />
      <Route path={PROGRAMS} name={PROGRAMS} getComponent={ProgramsPage} />
      <Route path={SPECIALIZATIONS} name={SPECIALIZATIONS} getComponent={SpecializationsPage} />
    </Route>

    {/* Analytics */}
    <Route name={ANALYTICS} path="analytics" getComponent={InstitutionsAdmin}>
      <IndexRedirect to={MONITOR} />

      <Route name={MONITOR} path={MONITOR} getComponent={MonitorPartnerPage} />

      <Route name={REACH} path={REACH} getComponent={ReachPartnerPage} />

      <Route name={DISBURSEMENTS} path={DISBURSEMENTS} getComponent={DisbursementsPartnerPage} />

      <Route name={COURSE_MARKETING} path={COURSE_MARKETING} getComponent={CourseMarketing}>
        <IndexRedirect to={SUMMARY} />
        <Route name={SUMMARY} path={SUMMARY} getComponent={MarketingSummaryPage} />
        <Route name={CHANNELS} path={CHANNELS} getComponent={ChannelsAnalyticsPage} />
        <Route name={CAMPAIGNS} path={CAMPAIGNS} getComponent={CampaignsAnalyticsPage} />
        <Route name={COURSE_VIEW} path={COURSE_VIEW} getComponent={CoursesAnalyticsPage} />
        <Route name={PROMOTION_VIEW} path={PROMOTION_VIEW} getComponent={PromotionsAnalyticsPage} />
      </Route>

      <Route name={SPECIALIZATION_MARKETING} path={SPECIALIZATION_MARKETING} getComponent={SpecializationMarketing}>
        <IndexRedirect to={SPECIALIZATION_SUMMARY} />
        <Route name={SPECIALIZATION_SUMMARY} path={SPECIALIZATION_SUMMARY} getComponent={S12nSummaryPage} />
        <Route name={SPECIALIZATION_CHANNELS} path={SPECIALIZATION_CHANNELS} getComponent={S12nChannelsAnalyticsPage} />
        <Route
          name={SPECIALIZATION_CAMPAIGNS}
          path={SPECIALIZATION_CAMPAIGNS}
          getComponent={S12nCampaignsAnalyticsPage}
        />
        <Route name={SPECIALIZATION_ANALYTICS} path={SPECIALIZATION_ANALYTICS} getComponent={S12nAnalyticsPage} />
        <Route
          name={SPECIALIZATION_PROMOTION}
          path={SPECIALIZATION_PROMOTION}
          getComponent={S12nPromotionsAnalyticsPage}
        />
      </Route>

      <Route name={ORG} path={ORG}>
        <IndexRoute getComponent={OrgCoursesAnalyticsPage} />
        <Route name={ORG_LEARNERS} path={ORG_LEARNERS} getComponent={OrgLearnersAnalyticsPage} />
        <Route name={OVERVIEW} path={OVERVIEW} getComponent={OrgCoursesAnalyticsPage} />
      </Route>
    </Route>

    {/* Institutions */}
    <Route name={INSTITUTION} path="institution" getComponent={InstitutionsAdmin}>
      <IndexRedirect to={SETTINGS} />
      <Route name={SETTINGS} path={SETTINGS}>
        <IndexRedirect to={STAFF} />
        <Route name={STAFF} path={STAFF} getComponent={StaffManagementInstitution} />
        <Route name={INSTITUTION_ASSETS} path={INSTITUTION_ASSETS} getComponent={PartnerEditor} />
        <Redirect from={UNIVERSITY_ASSETS} to={INSTITUTION_ASSETS} />{' '}
        {/* Redirect the old university-assets route to the new institution-assets */}
        <Route name={CUSTOM_ROLES} path={CUSTOM_ROLES} getComponent={CustomRolesManagementPage} />
      </Route>

      <Route name={EXPORTS} path={EXPORTS} getComponent={ExportsPartnerPage} />
      {/* External Apps pages owned by Raptors */}
      <Route name={EXTERNAL_APPS} path={EXTERNAL_APPS} getComponent={ExternalAppsPage}>
        <IndexRedirect to={MANAGE_EXTERNAL_APPS} />
        <Route name={DISCOVER_EXTERNAL_APPS} path={DISCOVER_EXTERNAL_APPS} getComponent={DiscoverExternalAppsPage} />
        <Route name={MANAGE_EXTERNAL_APPS} path={MANAGE_EXTERNAL_APPS} getComponent={ManageExternalAppsPage} />
      </Route>
    </Route>

    {/* Messages */}
    <Route name={PROMOTIONS} getComponent={InstitutionsAdmin}>
      <IndexRedirect to={PROMOTIONS} />
      <Route name={PROMOTIONS} path={PROMOTIONS} getComponent={MarketingEmailsTool} />
    </Route>

    {AuthoringAiToolsRootRoute}

    {/* This catch-all *, status=404 route is important for SSR. */}
    <Route path="*" getComponent={NotFound} />
  </Route>
);
